import { Link } from "react-router-dom";
import { ArrowRight, Phone } from "lucide-react";
import { ShieldMark } from "./ShieldLogo";
import { EMAIL, PHONE, PHONE_TEL, track } from "../lib/api";
import { Reveal } from "./Reveal";

export const ConversionCta = () => (
  <section className="relative overflow-hidden border-t border-line bg-ink grid-lines">
    <div className="pointer-events-none absolute -top-32 left-1/2 h-64 w-[36rem] -translate-x-1/2 rounded-full bg-lime/10 blur-[120px]" />
    <div className="relative mx-auto max-w-4xl px-4 py-20 text-center sm:px-6 sm:py-24">
      <Reveal>
        <p className="eyebrow mb-4">Start Here</p>
        <h2 className="h-display text-4xl text-white sm:text-5xl lg:text-6xl">
          You don&apos;t have to figure it out alone.
        </h2>
        <p className="mx-auto mt-5 max-w-xl text-base text-zinc-400 sm:text-lg">
          Tell us what you&apos;re trying to protect. We&apos;ll help you understand the next step.
        </p>
        <div className="mt-9 flex flex-col items-center justify-center gap-4 sm:flex-row">
          <Link to="/contact" data-testid="footer-start-conversation-button" className="btn-lime w-full sm:w-auto">
            Start a Conversation <ArrowRight className="h-4 w-4" />
          </Link>
          <a
            href={PHONE_TEL}
            data-testid="footer-phone-cta"
            onClick={() => track("phone_click", { location: "conversion_footer" })}
            className="btn-ghost w-full sm:w-auto"
          >
            <Phone className="h-4 w-4" /> {PHONE}
          </a>
        </div>
        <p className="mt-6 font-mono text-xs uppercase tracking-[0.2em] text-zinc-500">
          No pressure. Clear answers. Real people.
        </p>
      </Reveal>
    </div>
  </section>
);

export const Footer = () => (
  <footer className="border-t border-line bg-ink-2">
    <div className="mx-auto grid max-w-7xl gap-10 px-4 py-14 sm:px-6 md:grid-cols-4">
      <div>
        <Link to="/" className="inline-flex items-center gap-3">
          <ShieldMark className="h-9 w-9" />
          <span className="font-display text-lg font-bold uppercase text-white">
            Show Me <span className="text-lime">Shield</span>
          </span>
        </Link>
        <p className="mt-4 text-sm leading-relaxed text-zinc-400">
          Independent insurance brokerage proudly serving Missouri. Supplemental, life and health
          protection — explained in plain language.
        </p>
        <p className="mt-3 text-xs text-zinc-500">
          Show Me Shield is a DBA of Show Me Shield Benefits LLC.
        </p>
        <p className="mt-4 font-mono text-xs uppercase tracking-[0.2em] text-lime">
          Proudly serving Missouri
        </p>
      </div>

      <nav aria-label="Protection">
        <h3 className="font-display text-sm font-bold uppercase tracking-wider text-white">Protection</h3>
        <ul className="mt-4 space-y-2.5 text-sm text-zinc-400">
          <li><Link className="transition-colors hover:text-lime" to="/services#supplemental">Supplemental Insurance</Link></li>
          <li><Link className="transition-colors hover:text-lime" to="/services#supplemental">Accident &amp; Critical Illness</Link></li>
          <li><Link className="transition-colors hover:text-lime" to="/services#supplemental">Cancer &amp; Heart / Stroke</Link></li>
          <li><Link className="transition-colors hover:text-lime" to="/services#supplemental">Hospital Indemnity</Link></li>
          <li><Link className="transition-colors hover:text-lime" to="/services#life">Life Insurance</Link></li>
          <li><Link className="transition-colors hover:text-lime" to="/services#health">Individual &amp; Family Health</Link></li>
        </ul>
      </nav>

      <nav aria-label="Company">
        <h3 className="font-display text-sm font-bold uppercase tracking-wider text-white">Company</h3>
        <ul className="mt-4 space-y-2.5 text-sm text-zinc-400">
          <li><Link className="transition-colors hover:text-lime" to="/about">About Show Me Shield</Link></li>
          <li><Link className="transition-colors hover:text-lime" to="/faq">FAQ</Link></li>
          <li><Link className="transition-colors hover:text-lime" to="/contact">Contact</Link></li>
          <li><Link className="transition-colors hover:text-lime" to="/privacy">Privacy Policy</Link></li>
          <li><Link className="transition-colors hover:text-lime" to="/terms">Terms &amp; Disclosures</Link></li>
          <li><Link className="transition-colors hover:text-lime" to="/accessibility">Accessibility</Link></li>
        </ul>
      </nav>

      <div>
        <h3 className="font-display text-sm font-bold uppercase tracking-wider text-white">Talk With Us</h3>
        <a
          href={PHONE_TEL}
          data-testid="footer-phone-link"
          onClick={() => track("phone_click", { location: "footer" })}
          className="mt-4 flex items-center gap-2 font-mono text-lg font-bold text-lime"
        >
          <Phone className="h-4 w-4" /> {PHONE}
        </a>
        <a href={`mailto:${EMAIL}`} onClick={() => track("email_click", { location: "footer" })} className="mt-2 block text-sm text-zinc-400 transition-colors hover:text-lime">
          {EMAIL}
        </a>
        <p className="mt-4 text-sm text-zinc-500">No obligation. Start with a conversation.</p>
      </div>
    </div>

    <div className="border-t border-line">
      <div className="mx-auto max-w-7xl space-y-3 px-4 py-8 sm:px-6">
        <p data-testid="footer-disclosure" className="text-xs leading-relaxed text-zinc-500">
          Show Me Shield is a private independent insurance brokerage and is not affiliated with,
          endorsed by, or operated by the State of Missouri or any state or federal government agency
          or program. Show Me Shield Benefits LLC is not an insurance carrier; coverage is subject to the
          terms, conditions, exclusions and availability of each policy and carrier. Missouri insurance
          license information is available upon request. Information on this website is educational and
          is not an offer of insurance or a guarantee of eligibility or benefits.
        </p>
        <p className="text-xs text-zinc-600">
          &copy; {new Date().getFullYear()} Show Me Shield Benefits LLC &bull; ShowMeShield.com
        </p>
      </div>
    </div>
  </footer>
);
